/**
 * Card primitives. A card is a 2-character code `<rank><suit>`:
 *   ranks 3 4 5 6 7 8 9 T J Q K A 2 (low → high)
 *   suits D C H S (♦ < ♣ < ♥ < ♠)
 * Card value = rankIndex * 4 + suitIndex, so 3D = 0 and 2S = 51.
 */

export const RANKS = ['3', '4', '5', '6', '7', '8', '9', 'T', 'J', 'Q', 'K', 'A', '2'] as const;
export const SUITS = ['D', 'C', 'H', 'S'] as const;

export type Rank = (typeof RANKS)[number];
export type Suit = (typeof SUITS)[number];
export type Card = string;

export const SUIT_SYMBOL: Record<Suit, string> = { D: '♦', C: '♣', H: '♥', S: '♠' };
export const RANK_LABEL: Record<Rank, string> = {
  '3': '3', '4': '4', '5': '5', '6': '6', '7': '7', '8': '8', '9': '9',
  T: '10', J: 'J', Q: 'Q', K: 'K', A: 'A', '2': '2',
};

const CARD_RE = /^(10|[2-9TJQKA])([DCHS])$/;

/** Normalises user input (`10h`, `th`, `TH`) to canonical code (`TH`). Returns null if invalid. */
export function normalizeCard(input: unknown): Card | null {
  if (typeof input !== 'string' || input.length > 3) return null;
  const m = CARD_RE.exec(input.toUpperCase());
  if (!m) return null;
  const rank = m[1] === '10' ? 'T' : m[1];
  return rank + m[2];
}

export function isCard(input: unknown): input is Card {
  return typeof input === 'string' && normalizeCard(input) === input;
}

export function rankOf(card: Card): Rank {
  if (typeof card === 'string' && card.length >= 3 && card.startsWith('10')) return 'T';
  return card[0] as Rank;
}

export function suitOf(card: Card): Suit {
  if (typeof card === 'string' && card.length >= 3) return card[card.length - 1] as Suit;
  return card[1] as Suit;
}

export function rankIndex(card: Card): number {
  return RANKS.indexOf(rankOf(card));
}

export function suitIndex(card: Card): number {
  return SUITS.indexOf(suitOf(card));
}

export function cardValue(card: Card): number {
  return rankIndex(card) * 4 + suitIndex(card);
}

export function compareCards(a: Card, b: Card): number {
  return cardValue(a) - cardValue(b);
}

/** Ascending by rank then suit. Returns a new array. */
export function sortByRank(cards: readonly Card[]): Card[] {
  return [...cards].sort(compareCards);
}

/** Grouped by suit (♦ ♣ ♥ ♠), ascending rank within each suit. Returns a new array. */
export function sortBySuit(cards: readonly Card[]): Card[] {
  return [...cards].sort((a, b) => suitIndex(a) - suitIndex(b) || rankIndex(a) - rankIndex(b));
}

/**
 * Orders cards by pairs first (ranks with 2 or more cards, in ascending rank order),
 * followed by all non-pair cards (singletons) in ascending rank order.
 * Returns a new array.
 */
export function sortByPairs(cards: readonly Card[]): Card[] {
  const counts: Record<string, Card[]> = {};
  for (const c of cards) {
    const r = rankOf(c);
    (counts[r] ??= []).push(c);
  }

  for (const r of Object.keys(counts)) {
    counts[r].sort(compareCards);
  }

  const pairs: Card[] = [];
  const singles: Card[] = [];

  for (const r of RANKS) {
    const group = counts[r];
    if (!group || group.length === 0) continue;
    if (group.length >= 2) {
      pairs.push(...group);
    } else {
      singles.push(...group);
    }
  }

  return [...pairs, ...singles];
}

export function highestCard(cards: readonly Card[]): Card {
  return cards.reduce((hi, c) => (compareCards(c, hi) > 0 ? c : hi));
}

export function lowestCard(cards: readonly Card[]): Card {
  return cards.reduce((lo, c) => (compareCards(c, lo) < 0 ? c : lo));
}

export function fullDeck(): Card[] {
  const deck: Card[] = [];
  for (const r of RANKS) for (const s of SUITS) deck.push(r + s);
  return deck;
}

export type Rng = () => number;

/**
 * Cryptographically secure random float in [0, 1) using OS entropy.
 * Fallbacks to Math.random() only if Web Crypto is unavailable.
 */
export function cryptoRng(): number {
  if (typeof globalThis.crypto !== 'undefined' && typeof globalThis.crypto.getRandomValues === 'function') {
    const buf = new Uint32Array(2);
    globalThis.crypto.getRandomValues(buf);
    const high = buf[0] >>> 5; // 27 bits
    const low = buf[1] >>> 6;  // 26 bits
    return (high * 67108864 + low) * (1 / 9007199254740992);
  }
  return Math.random();
}

/**
 * Uniform integer in [0, maxExclusive) using rejection sampling to eliminate modulo bias.
 */
export function cryptoRandomInt(maxExclusive: number, rng?: Rng): number {
  if (maxExclusive <= 1) return 0;
  if (rng && rng !== cryptoRng) {
    return Math.floor(rng() * maxExclusive);
  }
  if (typeof globalThis.crypto !== 'undefined' && typeof globalThis.crypto.getRandomValues === 'function') {
    const limit = Math.floor(0x100000000 / maxExclusive) * maxExclusive;
    const buf = new Uint32Array(1);
    let val: number;
    do {
      globalThis.crypto.getRandomValues(buf);
      val = buf[0];
    } while (val >= limit);
    return val % maxExclusive;
  }
  return Math.floor(Math.random() * maxExclusive);
}

/** Fisher–Yates (Knuth) shuffle with an injectable RNG (defaults to CSPRNG with rejection sampling). */
export function shuffle<T>(items: readonly T[], rng: Rng = cryptoRng): T[] {
  const a = [...items];
  for (let i = a.length - 1; i > 0; i--) {
    const j = rng === cryptoRng ? cryptoRandomInt(i + 1) : Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Deterministic seeded RNG (mulberry32) for tests and reproducible deals. */
export function seededRng(seed: number): Rng {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6d2b79f5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

export function cardLabel(card: Card): string {
  return RANK_LABEL[rankOf(card)] + SUIT_SYMBOL[suitOf(card)];
}
